import React from "react";
import { Form, InputNumber, Checkbox, Select, Button } from "antd";
import styled from "styled-components";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

interface Option {
  label: string;
  value: string;
}

interface Apartment {
  name: string | null;
  type_de_cuisine: string | null;
  nbr_de_chambres_min: number | null;
  nbr_de_chambres_max: number | null;
  nbr_de_salles_de_bain_min: number | null;
  nbr_de_salles_de_bain_max: number | null;
  balcon_terasse: boolean;
  surface_min: number | null;
  surface_max: number | null;
}

interface Props {
  form: any;
  formData: any;
  setFormData: any;
}

const typeDeProjetOptions: Record<string, Option[]> = {
  appartements: [
    { label: "Studios", value: "Studios" },
    { label: "F2", value: "F2" },
    { label: "F3", value: "F3" },
    { label: "F4", value: "F4" },
    { label: "F5", value: "F5" },
    { label: "Duplex", value: "Duplex" },
    { label: "Triplex", value: "Triplex" },
  ],
  bureaux: [
    { label: "Coworking", value: "Coworking" },
    { label: "Salles de conférences", value: "Salles de conférences" },
    { label: "Bureaux individuels", value: "Bureaux individuels" },
    { label: "Open space", value: "Open space" },
  ],
  villas: [
    { label: "Isolées", value: "Isolées" },
    { label: "Jumelées", value: "Jumelées" },
    { label: "En bande", value: "En bande" },
  ],
  "locaux commerciaux": [
    { label: "Centres commerciaux", value: "Centres commerciaux" },
    { label: "Pied d'immeuble", value: "Pied d'immeuble" },
    { label: "Showrooms", value: "Showrooms" },
  ],
};

const apartmentTypeToBedrooms: Record<string, number | null> = {
  F2: 2,
  F3: 3,
  F4: 4,
  F5: 5,
  Duplex: null,
  Studios: null,
  Triplex: null,
};

const AppartementsFormList: React.FC<Props> = ({
  form,
  formData,
  setFormData,
}) => {
  // Add this function to the component
const handleSurfaceChange = (index: number, field: string, value: number | null) => {
  const currentPrices = form.getFieldValue("appartements_prices") || [];
  const apartments = form.getFieldValue("appartements") || [];
  const currentPrice = currentPrices[index] || {};
  
  if (index >= currentPrices.length) return;
  
  const updatedPrices = [...currentPrices];
  
  if (field === "surface_min") {
    // If unit price is available, update total price
    if (currentPrice.prix_vente_unitaire_min && value) {
      updatedPrices[index] = {
        ...updatedPrices[index],
        prix_vente_min: Math.round(currentPrice.prix_vente_unitaire_min * value)
      };
    }
  } else if (field === "surface_max") {
    // If unit price is available, update total price
    if (currentPrice.prix_vente_unitaire_max && value) {
      updatedPrices[index] = {
        ...updatedPrices[index],
        prix_vente_max: Math.round(currentPrice.prix_vente_unitaire_max * value)
      };
    }
  }
  
  form.setFieldsValue({ appartements_prices: updatedPrices });
  setFormData((prev: any) => ({
    ...prev,
    appartements_prices: updatedPrices,
  }));
};

  return (
    <>
      <Form.List name="appartements" >
        {(fields, { add, remove }) => {
          if (fields.length === 0) {
            add({
              name: null,
              type_de_cuisine: null,
              nbr_de_chambres_min: null,
              nbr_de_chambres_max: null,
              nbr_de_salles_de_bain_min: null,
              nbr_de_salles_de_bain_max: null,
              balcon_terasse: false,
              surface_min: null,
              surface_max: null,
            });
          }
          return (
            <>
              <ScrollableTableWrapper>
                <table
                  style={{
                    minWidth: "1200px",
                    width: "100%",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          ...headerStyle,
                          width: "50px",
                          borderBottom: "none",
                          borderLeft: "none",
                          borderTop: "none",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Button
                          color="primary"
                          variant="solid"
                          onClick={() => {
                            add({
                              name: null,
                              type_de_cuisine: null,
                              nbr_de_chambres_min: null,
                              nbr_de_chambres_max: null,
                              nbr_de_salles_de_bain_min: null,
                              nbr_de_salles_de_bain_max: null,
                              balcon_terasse: false,
                              surface_min: null,
                              surface_max: null,
                            });
                          }}
                          icon={<PlusOutlined />}
                        />
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        Programmation
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        Type de cuisine
                      </th>
                      <th
                        style={{ ...headerStyle, borderBottom: "none" }}
                        colSpan={2}
                      >
                        Chambres
                      </th>
                      <th
                        style={{ ...headerStyle, borderBottom: "none" }}
                        colSpan={2}
                      >
                        Salles de Bain
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        Balcon/Terrasse
                      </th>
                      <th
                        style={{ ...headerStyle, borderBottom: "none" }}
                        colSpan={2}
                      >
                        Surface (m²)
                      </th>
                    </tr>
                    <tr>
                      <th
                        style={{
                          ...subHeaderStyle,
                          borderTop: "none",
                          backgroundColor: "#fff",
                        }}
                      ></th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                      <th style={subHeaderStyle}>Min</th>
                      <th style={subHeaderStyle}>Max</th>
                      <th style={subHeaderStyle}>Min</th>
                      <th style={subHeaderStyle}>Max</th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                      <th style={subHeaderStyle}>Min</th>
                      <th style={subHeaderStyle}>Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map(({ key, name, ...restField }) => {
                      const currentValue = form.getFieldValue([
                        "appartements",
                        name,
                        "name",
                      ]);
                      return (
                        <tr key={key}>
                          <td style={cellStyle}>
                            <Button
                              variant="solid"
                              color="danger"
                              disabled={fields.length === 1}
                              // Update the remove method:
                              onClick={() => {
                                remove(name);

                                // After removing apartment, update the prices array
                                setTimeout(() => {
                                  const currentApartments =
                                    form.getFieldValue("appartements") || [];
                                  const currentPrices =
                                    form.getFieldValue("appartements_prices") ||
                                    [];

                                  // Remove the corresponding price entry
                                  const newPrices = currentPrices.filter(
                                    (_: any, index: number) => index !== name
                                  );

                                  form.setFieldsValue({
                                    appartements_prices: newPrices,
                                  });
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    appartements_prices: newPrices,
                                  }));
                                }, 0); // Use setTimeout to ensure the apartment is removed first
                              }}
                              icon={<MinusCircleOutlined />}
                            />
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "name"]}
                              rules={[
                                {
                                  required: true,message:""}
                              ]}
                            >
                              <Select
                                style={{ width: "100%" }}
                                placeholder="Select type"
                                value={currentValue}
                                
                                options={typeDeProjetOptions.appartements.map(
                                  (option) => {
                                    const currentApartments =
                                      form.getFieldValue("appartements") || [];
                                    const isDisabled = currentApartments.some(
                                      (a: Apartment, idx: number) =>
                                        idx !== name && a.name === option.value
                                    );
                                    return { ...option, disabled: isDisabled };
                                  }
                                )}
                                onChange={(value) => {
                                  // Existing logic to set the name field
                                  if (!currentValue) {
                                    form.setFields([
                                      {
                                        name: ["appartements", name, "name"],
                                        value,
                                      },
                                    ]);
                                  }

                                  // Determine the number of bedrooms based on the selected type
                                  let bedroomCount: number | null = null;
                                  if (apartmentTypeToBedrooms[value]) {
                                    bedroomCount =
                                      apartmentTypeToBedrooms[value]; // F2, F3, F4, F5
                                  } else if (
                                    ["studios", "duplex", "triplex"].includes(
                                      value
                                    )
                                  ) {
                                    bedroomCount = null; // Studios, Duplex, Triplex
                                  }

                                  // Update the form state using form.setFieldsValue
                                  form.setFieldsValue({
                                    appartements: form
                                      .getFieldValue("appartements")
                                      .map(
                                        (
                                          apartment: Apartment,
                                          index: number
                                        ) => {
                                          if (index === name) {
                                            return {
                                              ...apartment,
                                              name: value,
                                              nbr_de_chambres_min: bedroomCount,
                                              nbr_de_chambres_max: bedroomCount,
                                            };
                                          }
                                          return apartment;
                                        }
                                      ),
                                  });

                                  // Update the custom state (formData) using setFormData
                                  const updatedApartments = form
                                    .getFieldValue("appartements")
                                    .map(
                                      (apartment: Apartment, index: number) => {
                                        if (index === name) {
                                          return {
                                            ...apartment,
                                            name: value,
                                            nbr_de_chambres_min: bedroomCount,
                                            nbr_de_chambres_max: bedroomCount,
                                          };
                                        }
                                        return apartment;
                                      }
                                    );

                                  setFormData((prevFormData: any) => ({
                                    ...prevFormData,
                                    appartements: updatedApartments,
                                  }));
                                }}
                              />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "type_de_cuisine"]}
                              rules={[
                                {
                                  required: true,message:""}
                              ]}
                            >
                              <Select
                                style={{ width: "100%" }}
                                placeholder="Type de cuisine"
                              >
                                <Select.Option value="fermee">
                                  fermée
                                </Select.Option>
                                <Select.Option value="americaine">
                                  américaine
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "nbr_de_chambres_min"]}
                              rules={[{ required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const max = getFieldValue([
                                      "appartements",
                                      name,
                                      "nbr_de_chambres_max",
                                    ]);
                                    if (!value || !max || value <= max) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber
                                min={0}
                                style={{ width: "100%" }}
                                disabled={["F2", "F3", "F4", "F5"].includes(
                                  form.getFieldValue([
                                    "appartements",
                                    name,
                                    "name",
                                  ])
                                )}
                              />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "nbr_de_chambres_max"]}
                              rules={[{ required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const min = getFieldValue([
                                      "appartements",
                                      name,
                                      "nbr_de_chambres_min",
                                    ]);
                                    if (!value || !min || value >= min) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber
                                min={0}
                                style={{ width: "100%" }}
                                disabled={["F2", "F3", "F4", "F5"].includes(
                                  form.getFieldValue([
                                    "appartements",
                                    name,
                                    "name",
                                  ])
                                )}
                              />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "nbr_de_salles_de_bain_min"]}
                              rules={[{ required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const max = getFieldValue([
                                      "appartements",
                                      name,
                                      "nbr_de_salles_de_bain_max",
                                    ]);
                                    if (!value || !max || value <= max) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber min={0} style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "nbr_de_salles_de_bain_max"]}
                              rules={[{ required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const min = getFieldValue([
                                      "appartements",
                                      name,
                                      "nbr_de_salles_de_bain_min",
                                    ]);
                                    if (!value || !min || value >= min) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber min={0} style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "balcon_terasse"]}
                              valuePropName="checked"
                            >
                              <Checkbox />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "surface_min"]}
                              rules={[{ required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const max = getFieldValue([
                                      "appartements",
                                      name,
                                      "surface_max",
                                    ]);
                                    if (!value || !max || value <= max) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber onChange={(value) => handleSurfaceChange(name, "surface_min", value)} min={0} style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "surface_max"]}
                              rules={[{ required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const min = getFieldValue([
                                      "appartements",
                                      name,
                                      "surface_min",
                                    ]);
                                    if (!value || !min || value >= min) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber onChange={(value) => handleSurfaceChange(name, "surface_max", value)} min={0} style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollableTableWrapper>
            </>
          );
        }}
      </Form.List>
    </>
  );
};

const headerStyle = {
  border: "2px solid #42005a",
  padding: "3px",
  fontSize: "14px",
  fontWeight: "bold",
  color: "#42005a",
  backgroundColor: "#eeedf0",
  width: "150px",
};

const subHeaderStyle = {
  ...headerStyle,
  fontSize: "12px",
  borderLeft: "none",
};

const cellStyle = {
  border: "2px solid #42005a",
  padding: "3px",
  textAlign: "center" as const,
};

const ScrollableTableWrapper = styled.div`
  overflow-x: auto;
  padding-bottom: 8px;
  margin-top: 16px;

  &::-webkit-scrollbar {
    height: 16px;
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #42005a;
    border-radius: 4px;
    &:hover {
      background-color: #603570;
    }
  }

  &::-webkit-scrollbar-track {
    background-color: #f1f1f1;
    border-radius: 4px;
  }

  scrollbar-width: thin;
  scrollbar-color: #42005a #f1f1f1;
`;

export default AppartementsFormList;
